import {
  Box, Button,
  Container,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  makeStyles,
  Theme,
  Typography,
  withStyles
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle/DialogTitle";
import CloseIcon from '@material-ui/icons/Close';
import {useDispatch, useSelector} from "react-redux";
import {onCallDeleteClipAPI, getStimulusAnalysis, initAnalysisData} from "../../../store/actions/creator/analysis";
import {useEffect, useState} from "react";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import {apiClient} from "../../../config/apiClient";

const styles = (theme: Theme) =>
  createStyles({
    tagmodalheader: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    closeButton: {
      color: theme.palette.grey[500],
      fontFamily: 'roboto-bold',
    },
    closeBtnIcon: {
      color: '#212D4080',
      width: 24,
      height: 24,
      fontFamily: 'roboto-bold'
    },
    modaltitle: {
      fontFamily: 'Spartan-bold',
      fontSize: 24,
      color: '#212D40',
      textAlign: 'left'
    }
  });

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#F0F0F0',
    paddingTop: "30px",
    fontFamily: 'roboto-regular',
    height: '100%',
    overflowX: 'hidden',
  },
  content: {
    minHeight: '100%',
    padding: theme.spacing(4),
    paddingBottom: 100,
    paddingTop: theme.spacing(6),
    '@media (min-width: 2000px)': {
      width: 1600
    },
  },
  dialogWrap: {
    margin: 'auto',
    maxWidth: 500,
    borderRadius: 14,
  },
  actionBtn: {
    textAlign: 'right',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  contentDialog: {
    fontFamily: 'roboto-regular',
    fontSize: 14,
    color: '#697683'
  },
  delBtn: {
    backgroundColor: '#ED564A',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: 'white',
    boxShadow: 'none',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#ED4F59'
    }
  },
  cancelBtn: {
    backgroundColor: '#F0F0F0',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    boxShadow: 'none',
    textTransform: 'none'
  },
  btn: {
    margin: 8
  },
  experienceName: {
    marginTop: '12px',
    fontWeight: 'bold',
    fontFamily: 'roboto-regular',
  },
}));

const DialogTitle = withStyles(styles)((props: any) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.tagmodalheader} {...other}>
      <Typography variant="h5" className={classes.modaltitle}>{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon classes={{ root: classes.closeBtnIcon }} />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

interface IProps {
  issearchpage: boolean,
  selectedClip: any;
  open: boolean;
  onCloseDeleteModal: () => void
}

const DeleteClipModal = (props: IProps) => {
  const classes = useStyles();
  const { issearchpage, selectedClip, open, onCloseDeleteModal } = props;
  const dispatch = useDispatch();
  const { stimulus } = useSelector((state: any) => state.analysis);
  const loginUserData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : "";
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setModalLoading(false);
    }
  }, [open]);

  const onDeleteClip = async () => {
    setModalLoading(true);
    const params = {
      tenant_id: loginUserData.customer_id,
      session_code: selectedClip.session_code,
      session_type: selectedClip.session_type,
      stimulus_id: selectedClip.session_type === 'ASYNC' && stimulus ? stimulus.stimulus_id : '',
      clip_id: selectedClip.clip_id,
      search: issearchpage,
    };

    if(selectedClip.session_type === 'ASYNC' && stimulus === null){
      dispatch(initAnalysisData());
      const res = await apiClient(true).get(`/stimulus/v2/stimulus/${loginUserData.customer_id}/${selectedClip.session_code}`);
      dispatch(getStimulusAnalysis(res.data && res.data[0]));
      if(res.data ){
        params.stimulus_id = res.data[0].stimulus_id;
      }
    }

    dispatch(onCallDeleteClipAPI(params, (res: any) => {
      if (res) {
        setTimeout(() => {
          onCloseDeleteModal();
          setModalLoading(false);
        }, 2000);
      } else {
        onCloseDeleteModal();
        setModalLoading(false);
      }
    }))
  };

  const closeDeleteModal = () => {
    onCloseDeleteModal();
  };

  return (
    <Dialog
      disableEscapeKeyDown
      open={open}
      keepMounted
      fullWidth={true}
      maxWidth={"sm"}
      classes={{
        paper: classes.dialogWrap
      }}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle id="alert-dialog-slide-title" onClose={closeDeleteModal}>
        {"Delete Clip?"}
      </DialogTitle>
      <Divider />
      <DialogContent style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Container maxWidth="md">
          <Typography className={classes.contentDialog}>Are you sure you want to delete the Clip: <br /></Typography>
          <Typography className={classes.experienceName}>{selectedClip && selectedClip.name}</Typography>
          <br/>
          <Typography className={classes.contentDialog}>This operation cannot be undone.</Typography>
        </Container>
      </DialogContent>
      <DialogActions>
        <Container maxWidth="md" className={classes.actionBtn}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Box mr={2}>
              <Button
                onClick={closeDeleteModal}
                variant="contained"
                className={classes.cancelBtn}
              >
                Cancel
              </Button>
            </Box>
            <Button
              onClick={onDeleteClip}
              variant="contained"
              color="secondary"
              className={classes.delBtn}
            >
              {modalLoading ? <CircularProgress color="inherit" size={20}/> : 'Delete'}
            </Button>
          </Box>
        </Container>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteClipModal;